<template>
  <section
    class="rich-media-lesson"
    :class="[richMediaClasses.root, { 'rich-media-lesson--h5': isH5 }]"
    :data-mg-rich-media-contract="richMediaContractVersion"
  >
    <div v-if="loading" class="rich-media-lesson__state" :class="[richMediaClasses.state, richMediaClasses.loading]">
      正在加载图文课…
    </div>
    <div v-else-if="error" class="rich-media-lesson__state" :class="[richMediaClasses.state, richMediaClasses.error]">
      {{ error }}
      <button class="rich-media-lesson__retry" @click="load">重试</button>
    </div>
    <template v-else>
      <div v-if="showUpdateBanner" class="rich-media-lesson__update" :class="richMediaClasses.update">
        课程内容已更新，建议重新阅读
        <button @click="showUpdateBanner = false">知道了</button>
      </div>
      <article class="rich-media-lesson__content" :class="richMediaClasses.content">
        <template v-for="block in document.blocks">
          <section
            v-if="block.type === 'rich_text'"
            :key="block.id"
            class="rich-media-lesson__text"
            :class="richMediaBlockClasses(block.type, block.tone)"
          >
            <rich-media-node v-for="(node, index) in block.nodes || []" :key="index" :node="node" />
          </section>
          <aside
            v-else-if="block.type === 'callout'"
            :key="block.id"
            class="rich-media-lesson__callout"
            :class="[legacyCalloutClass(block.tone), richMediaBlockClasses(block.type, block.tone)]"
          >
            <span class="rich-media-lesson__callout-label" :class="richMediaClasses.calloutLabel">
              {{ calloutLabel(block.tone) }}
            </span>
            <rich-media-node v-for="(node, index) in block.nodes || []" :key="index" :node="node" />
          </aside>
          <figure
            v-else-if="block.type === 'image'"
            :key="block.id"
            class="rich-media-lesson__image"
            :class="[richMediaBlockClasses(block.type, block.tone), { 'is-original': block.display === 'original' }]"
          >
            <el-image
              v-if="mediaFor(block).status === 'ready' && !brokenImages[block.id]"
              class="rich-media-lesson__image-preview"
              :src="mediaFor(block).url"
              :alt="block.alt || ''"
              :preview-src-list="[mediaFor(block).url]"
              preview-teleported
              @error="$set(brokenImages, block.id, true)"
            />
            <!-- 不要给 el-image 加 loading="lazy"：Element UI 会把 $attrs 复制到脱离文档的 new Image() 上，Chrome 对其永不加载 -->
            <div
              v-else-if="brokenImages[block.id]"
              class="rich-media-lesson__offline"
              :class="[richMediaClasses.state, richMediaClasses.offline]"
            >
              图片加载失败，请稍后重试
            </div>
            <div v-else class="rich-media-lesson__offline" :class="[richMediaClasses.state, richMediaClasses.offline]">
              图片素材已下线
            </div>
            <figcaption v-if="block.alt" :class="richMediaClasses.caption">{{ block.alt }}</figcaption>
          </figure>
          <figure
            v-else-if="block.type === 'video'"
            :key="block.id"
            class="rich-media-lesson__video"
            :class="[richMediaBlockClasses(block.type, block.tone), videoFigureClass(block.id)]"
          >
            <div class="rich-media-lesson__video-box" :class="richMediaClasses.videoBox">
              <video
                v-if="mediaFor(block).status === 'ready'"
                controls
                playsinline
                preload="metadata"
                :controlsList="isH5 ? 'nodownload nofullscreen' : 'nodownload'"
                :poster="mediaFor(block).cover_url || ''"
                :src="mediaFor(block).url"
                @contextmenu.prevent
                @loadedmetadata="onVideoMetadata(block.id, $event.target)"
                @play="$set(playingVideos, block.id, true)"
                @pause="$set(playingVideos, block.id, false)"
                @ended="$set(playingVideos, block.id, false)"
              ></video>
              <!-- 居中大播放键：未播放 / 暂停 / 播完时显示，播放中隐藏，不遮挡原生控制条 -->
              <button
                v-if="mediaFor(block).status === 'ready' && !playingVideos[block.id]"
                type="button"
                class="rich-media-lesson__video-play"
                :class="richMediaClasses.control"
                aria-label="播放"
                @click="playVideo($event)"
              >
                <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M8 5.5v13l11-6.5z" /></svg>
              </button>
              <!-- H5 走自定义全屏：浏览器原生全屏在竖屏手机上只会把横屏视频居中留黑边 -->
              <button
                v-if="isH5 && mediaFor(block).status === 'ready'"
                type="button"
                class="rich-media-lesson__video-fs"
                :class="richMediaClasses.control"
                @click="toggleVideoFullscreen(block.id, $event)"
              >
                {{ fullscreenBlockId === block.id ? '退出全屏' : '全屏' }}
              </button>
              <div
                v-if="mediaFor(block).status !== 'ready'"
                class="rich-media-lesson__offline"
                :class="[richMediaClasses.state, richMediaClasses.offline]"
              >
                视频素材已下线
              </div>
            </div>
            <figcaption v-if="block.alt" :class="richMediaClasses.caption">{{ block.alt }}</figcaption>
          </figure>
          <hr
            v-else-if="block.type === 'divider'"
            :key="block.id"
            class="rich-media-lesson__divider"
            :class="richMediaClasses.divider"
          />
          <!-- 未来新增的区块类型：不报错、不吞内容，明确提示 -->
          <div
            v-else-if="!isKnownBlockType(block.type)"
            :key="block.id"
            class="rich-media-lesson__offline"
            :class="[richMediaClasses.state, richMediaClasses.unsupported]"
          >
            当前版本不支持该内容，请升级后查看
          </div>
        </template>
        <div v-if="!document.blocks.length" class="rich-media-lesson__state" :class="richMediaClasses.state">
          本课时暂无内容
        </div>
      </article>
      <div class="rich-media-lesson__footer">
        <el-button v-if="!completed" type="primary" :loading="completing" @click="complete">
          标记为已完成
        </el-button>
        <div v-else class="rich-media-lesson__completed" :class="richMediaClasses.completed" role="status" aria-live="polite">
          <svg viewBox="0 0 24 24" aria-hidden="true"><path d="m5 12 4.5 4.5L19 7" /></svg>
          <span>已完成本课时学习</span>
        </div>
        <p v-if="!completed" class="rich-media-lesson__footer-tip">阅读完本课时内容后，点击按钮记录学习进度</p>
        <div v-if="completed" class="rich-media-lesson__next-actions">
          <el-button v-if="nextLesson" type="primary" class="rich-media-lesson__next" @click="$emit('change-lesson', nextLesson)">
            下一课：{{ nextLesson.lesson_name || '继续学习' }}
          </el-button>
          <el-button type="text" class="rich-media-lesson__back" @click="$emit('back-to-course')">返回课程目录</el-button>
        </div>
      </div>
    </template>
  </section>
</template>

<script>
import { isH5 } from '@saas/components/src/utils/utils'
import {
  getRichMediaLesson,
  completeRichMediaLesson,
  acknowledgeRichMediaContentUpdate
} from '@saas/service-api/src/learning-center'
import RichMediaNode from './RichMediaNode.vue'
import {
  normalizeDocument,
  indexResolvedMedia,
  mediaForBlock,
  isKnownBlockType,
  needsContentUpdateAck
} from './document'
import {
  RICH_MEDIA_CONTRACT_VERSION,
  RICH_MEDIA_CLASSES,
  RICH_MEDIA_CALLOUT_TONES,
  richMediaBlockClasses
} from './contract'

const CALLOUT_LABELS = { key_point: '重点', reminder: '提醒', example: '示例' }
const LEGACY_CALLOUT_CLASSES = {
  key_point: 'rich-media-lesson__callout--key_point',
  reminder: 'rich-media-lesson__callout--reminder',
  example: 'rich-media-lesson__callout--example'
}
const normalizeCalloutTone = (tone) => RICH_MEDIA_CALLOUT_TONES.includes(tone) ? tone : 'key_point'

// 图文课阅读器：只消费 /userCourse/rich-media-lesson/* 三个接口。
// 不挂载视频播放器、不发送 KeepAlive；完成状态由学员手动标记。
export default {
  name: 'RichMediaLesson',
  components: { RichMediaNode },
  props: {
    courseId: { type: [String, Number], required: true },
    lessonId: { type: [String, Number], required: true },
    nextLesson: { type: Object, default: null }
  },
  data() {
    return {
      richMediaContractVersion: RICH_MEDIA_CONTRACT_VERSION,
      richMediaClasses: RICH_MEDIA_CLASSES,
      isH5: isH5(),
      loading: true,
      error: '',
      detail: {},
      document: normalizeDocument(null),
      mediaByBlock: Object.create(null),
      completing: false,
      completed: false,
      showUpdateBanner: false,
      // All maps are keyed by server document block ids, so they must not inherit prototype keys.
      brokenImages: Object.create(null),
      loadSeq: 0,
      // H5 自定义全屏：当前全屏的视频区块、各视频的横/竖屏、视口是否竖屏
      fullscreenBlockId: '',
      videoLandscape: Object.create(null),
      playingVideos: Object.create(null),
      viewportPortrait: typeof window !== 'undefined' ? window.innerHeight > window.innerWidth : true
    }
  },
  watch: {
    lessonId() {
      this.load()
    }
  },
  mounted() {
    this.load()
  },
  beforeDestroy() {
    this.exitVideoFullscreen()
  },
  methods: {
    isKnownBlockType,
    richMediaBlockClasses,
    calloutLabel(tone) {
      return CALLOUT_LABELS[normalizeCalloutTone(tone)]
    },
    legacyCalloutClass(tone) {
      return LEGACY_CALLOUT_CLASSES[normalizeCalloutTone(tone)]
    },
    mediaFor(block) {
      return mediaForBlock(this.mediaByBlock, block)
    },
    onVideoMetadata(blockId, video) {
      this.$set(this.videoLandscape, blockId, video.videoWidth > video.videoHeight)
    },
    playVideo(event) {
      const figure = event && event.currentTarget && event.currentTarget.closest('figure')
      const video = figure && figure.querySelector('video')
      if (!video) return
      const playing = video.play()
      // 自动播放策略等原因拒绝时静默，按钮仍在，用户可再点
      if (playing && playing.catch) playing.catch(() => {})
    },
    videoFigureClass(blockId) {
      if (this.fullscreenBlockId !== blockId) return null
      // 沿用点播播放器在 H5 的语义（replay/Index.vue .replay-landscape）：
      // 横屏视频且手机竖持 → 旋转 90° 铺满；竖屏视频或手机已横持 → 直接铺满，不旋转
      const rotate = this.videoLandscape[blockId] !== false && this.viewportPortrait
      return { 'is-fullscreen': true, 'is-landscape': rotate }
    },
    toggleVideoFullscreen(blockId, event) {
      if (this.fullscreenBlockId === blockId) {
        this.exitVideoFullscreen()
        return
      }
      const figure = event && event.currentTarget && event.currentTarget.closest('figure')
      const video = figure && figure.querySelector('video')
      // 元数据未到时先按横屏处理（16:9 是绝大多数），loadedmetadata 后会自动修正
      if (video && video.videoWidth) this.onVideoMetadata(blockId, video)
      this.viewportPortrait = window.innerHeight > window.innerWidth
      this.fullscreenBlockId = blockId
      document.body.style.overflow = 'hidden'
      window.addEventListener('resize', this.handleViewportResize)
    },
    exitVideoFullscreen() {
      if (!this.fullscreenBlockId) return
      this.fullscreenBlockId = ''
      document.body.style.overflow = ''
      window.removeEventListener('resize', this.handleViewportResize)
    },
    handleViewportResize() {
      this.viewportPortrait = window.innerHeight > window.innerWidth
    },
    load() {
      this.exitVideoFullscreen()
      // 快速切换课时时，旧课时的响应可能后到；只接受最新一次请求的结果
      const seq = ++this.loadSeq
      this.loading = true
      this.error = ''
      getRichMediaLesson({ course_id: this.courseId, lesson_id: this.lessonId })
        .then((res) => {
          if (seq !== this.loadSeq) return
          if (res.code !== 1) {
            this.error = res.message || '图文课加载失败'
            return
          }
          this.detail = res.data || {}
          this.document = normalizeDocument(this.detail.content)
          this.mediaByBlock = indexResolvedMedia(this.detail.resolved_media)
          this.completed = !!this.detail.completed
          this.showUpdateBanner = needsContentUpdateAck(this.detail)
          // 页面成功渲染后即确认已读：本次仍展示提示，下次进入不再提示，直到下一次实质更新
          if (this.showUpdateBanner) this.$nextTick(this.acknowledgeUpdate)
        })
        .catch(() => {
          if (seq !== this.loadSeq) return
          this.error = '图文课加载失败，请稍后重试'
        })
        .finally(() => {
          if (seq !== this.loadSeq) return
          this.loading = false
        })
    },
    acknowledgeUpdate() {
      return acknowledgeRichMediaContentUpdate({
        course_id: this.courseId,
        lesson_id: Number(this.lessonId),
        content_update_version: this.detail.content_update_version
      }).catch(() => {})
    },
    complete() {
      if (this.completed) return
      this.completing = true
      completeRichMediaLesson({ course_id: this.courseId, lesson_id: Number(this.lessonId) })
        .then((res) => {
          if (res.code === 1) {
            this.completed = true
            this.$message && this.$message.success('已标记为完成')
            this.$emit('completed')
          } else {
            this.$message && this.$message.error(res.message || '操作失败')
          }
        })
        .catch(() => {
          // request.js 的全局提示已被注释掉：弱网超时不提示的话，按钮恢复得像什么都没发生
          this.$message && this.$message.error('网络异常，标记完成失败，请重试')
        })
        .finally(() => {
          this.completing = false
        })
    }
  }
}
</script>

<style src="./rich-media-content.scss" lang="scss"></style>

<style scoped lang="scss">
.rich-media-lesson {
  width: 100%;
  min-height: 420px;
  padding: 32px;
  border-radius: 8px;
  box-sizing: border-box;
  overflow-x: hidden;
}
.rich-media-lesson--h5 {
  padding: 16px;
  border-radius: 0;
  min-height: 60vh;
}
.rich-media-lesson__content {
  max-width: 780px;
  margin: 0 auto;
}
.rich-media-lesson__video-play {
  position: absolute;
  top: 50%;
  left: 50%;
  z-index: 2;
  width: 64px;
  height: 64px;
  padding: 0;
  border-radius: 50%;
  transform: translate(-50%, -50%);
  transition: background 0.2s;
  svg {
    display: block;
    width: 36px;
    height: 36px;
    margin: 0 auto;
    fill: currentColor;
  }
}
.rich-media-lesson--h5 .rich-media-lesson__video-play {
  width: 56px;
  height: 56px;
  svg {
    width: 30px;
    height: 30px;
  }
}
.rich-media-lesson__video-fs {
  position: absolute;
  top: 8px;
  right: 8px;
  z-index: 2;
  min-width: 40px;
  min-height: 40px;
  padding: 4px 10px;
  border-radius: 14px;
  font-size: 12px;
  line-height: 18px;
}
// H5 上只保留我们的「全屏」入口：controlsList=nofullscreen 在 Chrome 只是把原生按钮置灰，
// 留着会让人以为坏了；用 WebKit/Blink 的控件伪元素把它整个藏掉（Chrome / Safari / 微信 X5 都认）
.rich-media-lesson--h5 .rich-media-lesson__video video::-webkit-media-controls-fullscreen-button {
  display: none !important;
}
// H5 自定义全屏（与点播播放器 .replay-landscape / .replay-portrait-fullscreen 同一套语义）
// is-fullscreen：固定层铺满视口；is-landscape：横屏视频在竖持手机上旋转 90°，宽取 100vh、高取 100vw
.rich-media-lesson__video.is-fullscreen {
  position: fixed;
  top: 0;
  left: 0;
  z-index: 999999;
  width: 100vw;
  height: 100vh;
  margin: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #000;
  .rich-media-lesson__video-box {
    width: 100%;
    height: 100%;
    aspect-ratio: auto;
    border-radius: 0;
    background: #000;
  }
  video {
    width: 100%;
    height: 100%;
    max-width: none;
    aspect-ratio: auto;
    border-radius: 0;
    background: #000;
    object-fit: contain;
  }
  figcaption {
    display: none;
  }
}
.rich-media-lesson__video.is-fullscreen.is-landscape {
  width: 100vh;
  height: 100vw;
  transform-origin: top left;
  transform: rotate(90deg) translateY(-100%);
}
.rich-media-lesson__image-preview {
  display: block;
  width: fit-content;
  max-width: 100%;
  margin: 0 auto;
  cursor: zoom-in;
}
.rich-media-lesson__image-preview :deep(.el-image__inner) {
  display: block;
  width: auto;
  max-width: 100%;
  max-height: 520px;
  border-radius: 8px;
  object-fit: contain;
}
.rich-media-lesson__image.is-original .rich-media-lesson__image-preview :deep(.el-image__inner) {
  max-height: none;
}
.rich-media-lesson__retry {
  margin-left: 12px;
  border: 0;
  color: var(--mg-rich-media-action);
  background: transparent;
  cursor: pointer;
}
.rich-media-lesson__update {
  max-width: 780px;
  margin: 0 auto 16px;
}
.rich-media-lesson__update button {
  margin-left: 12px;
  border: 0;
  color: var(--mg-rich-media-action);
  background: transparent;
  cursor: pointer;
}
.rich-media-lesson__footer {
  max-width: 780px;
  margin: 32px auto 0;
  text-align: center;
}
.rich-media-lesson__footer-tip {
  margin-top: 10px;
  font-size: 13px;
  color: var(--mg-rich-media-text-muted);
}
.rich-media-lesson__completed {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  font-weight: 600;
}
.rich-media-lesson__completed svg {
  width: 20px;
  height: 20px;
  fill: none;
  stroke: currentColor;
  stroke-linecap: round;
  stroke-linejoin: round;
  stroke-width: 2.5;
}
.rich-media-lesson__next-actions {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  margin-top: 16px;
}
.rich-media-lesson__next {
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
}
.rich-media-lesson__back {
  color: var(--mg-rich-media-action);
}
</style>
